import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Typography,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import BusinessIcon from "@mui/icons-material/Business";
import SchoolIcon from "@mui/icons-material/School";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import DynamicDeskLogo from "../assets/DD_LOGO_SCHWARZ.png";

/**
 * ProductSelectionPopup Component
 *
 * Shows a popup when user has multiple subscribed products.
 * Displays radio buttons only for products the user is subscribed to.
 * Filters available options based on the subscribedProducts prop.
 * Only calls API if selected product differs from current product.
 */
const ProductSelectionPopup = ({
  open,
  onClose,
  onConfirm,
  currentProduct,
  subscribedProducts = [],
  loading = false,
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState(""); // Force user to make a selection

  // Product options with their display info
  const productOptions = [
    {
      value: "corporate",
      label: t("corporateProduct") || "Corporate",
      icon: <BusinessIcon sx={{ fontSize: 24, color: "#063bd0" }} />,
      description: t("corporateDescription") || "Workplace ergonomics solution",
    },
    {
      value: "preventive_course",
      label: t("preventiveCourseProduct") || "Preventive Course",
      icon: <SchoolIcon sx={{ fontSize: 24, color: "#063bd0" }} />,
      description:
        t("preventiveCourseDescription") || "Educational prevention program",
    },
    {
      value: "physio_therapist",
      label: t("physioProduct") || "Physio",
      icon: <LocalHospitalIcon sx={{ fontSize: 24, color: "#063bd0" }} />,
      description: t("physioDescription") || "Professional physiotherapy tools",
    },
    {
      value: "physio_user",
      label: t("physioUserProduct") || "Physio User",
      icon: <DirectionsRunIcon sx={{ fontSize: 24, color: "#063bd0" }} />,
      description:
        t("physioUserDescription") || "Access for registered physio users",
    },
  ];

  // Filter options to show only subscribed products
  const availableOptions = productOptions.filter((option) => {
    // If no subscribedProducts provided, show all options (fallback)
    if (!subscribedProducts || subscribedProducts.length === 0) {
      return true;
    }

    // Check if this product is in the user's subscribed products
    return subscribedProducts.some(
      (subscribedProduct) => subscribedProduct.product_name === option.value
    );
  });

  const handleProductChange = (event) => {
    const newValue = event.target.value;
    setSelectedProduct(newValue);
  };

  const handleConfirm = () => {
    // Always call onConfirm, let the parent component handle the logic
    onConfirm(selectedProduct);
  };


  return (
    <Dialog
      open={open}
      onClose={() => {}} // Disable closing by clicking outside
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown // Disable ESC key closing
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <img
            src={DynamicDeskLogo}
            style={{
              width: "60px",
              height: "auto",
              marginBottom: "16px",
            }}
            alt="Dynamic Desk Logo"
          />
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#063bd0", fontWeight: "bold" }}
          >
            {t("selectYourProduct") || "Select Your Product"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("multipleProductsAvailable") ||
              "You have access to multiple products. Please select the one you want to use."}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={selectedProduct}
            onChange={handleProductChange}
            sx={{ gap: 1.5 }}
          >
            {availableOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    sx={{
                      color: "rgba(0, 0, 0, 0.6)",
                      "&.Mui-checked": { color: "#063bd0" },
                    }}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      borderRadius: 1,
                      border:
                        selectedProduct === option.value
                          ? "2px solid #063bd0"
                          : "1px solid rgba(0, 0, 0, 0.12)",
                      backgroundColor:
                        selectedProduct === option.value
                          ? "rgba(6, 59, 208, 0.04)"
                          : "transparent",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(6, 59, 208, 0.04)",
                        borderColor: "#063bd0",
                      },
                    }}
                  >
                    <Box sx={{ minWidth: 30 }}>{option.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "text.primary", mb: 0.5, fontWeight: 500 }}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{
                  margin: 0,
                  "& .MuiFormControlLabel-label": { width: "100%" },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Current Product Info */}
        {currentProduct && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "rgba(5, 182, 127, 0.1)",
              borderRadius: 1,
              border: "1px solid rgba(5, 182, 127, 0.2)",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: "text.secondary", mb: 1 }}
            >
              {t("currentlyActive") || "Currently active:"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#05b67f", fontWeight: 500 }}
            >
              {productOptions.find((opt) => opt.value === currentProduct)
                ?.label || currentProduct}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: "flex-end" }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !selectedProduct}
          sx={{
            backgroundColor: "#063bd0",
            "&:hover": {
              backgroundColor: "#05b67f",
            },
            minWidth: "140px",
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : selectedProduct === currentProduct ? (
            t("continue") || "Continue"
          ) : (
            t("switchProduct") || "Switch Product"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelectionPopup;
